import { create } from "zustand"
import { parseISO, startOfDay, isSameDay, isAfter } from "date-fns"
import type { UserEvent } from "@/services/api/cronos/user/events/get-all"
import formatToISODateTime from "@/modules/calendar/utils/format-to-ISO-date-time"

interface EventsState {
  events: UserEvent[]
}

interface EventsActions {
  setEvents: (events: UserEvent[]) => void
  addEvents: (newEvents: UserEvent[]) => void
  updateEvents: (newEvents: UserEvent[], isBulkUpdate: boolean) => void
  deleteEvents: (eventRef: UserEvent, isBulkDelete: boolean) => void
}

const initialState: EventsState = {
  events: [],
}

const useEvents = create<EventsState & EventsActions>()((set, get) => ({
  ...initialState,
  setEvents: (events) =>
    set({
      events: events.map((event) => ({
        ...event,
        startDate: formatToISODateTime(parseISO(event.startDate)),
        endDate: formatToISODateTime(parseISO(event.endDate)),
      })),
    }),
  addEvents: (newEvents) =>
    set({
      events: [
        ...get().events,
        ...newEvents.map((event) => ({
          ...event,
          startDate: formatToISODateTime(parseISO(event.startDate)),
          endDate: formatToISODateTime(parseISO(event.endDate)),
        })),
      ],
    }),
  updateEvents: (newEditedEvents, isBulkUpdate) => {
    const processedEvents = newEditedEvents.map((event) => ({
      ...event,
      startDate: formatToISODateTime(parseISO(event.startDate)),
      endDate: formatToISODateTime(parseISO(event.endDate)),
    }))

    return set({
      events: isBulkUpdate
        ? [
            ...get().events.filter(
              (event) => !event.parent || event.parent.id !== processedEvents[0]?.parent?.id,
            ),
            ...processedEvents,
          ]
        : get().events.map((event) => {
            if (processedEvents && processedEvents.length > 0 && processedEvents[0]) {
              return event.id !== processedEvents[0].id ? event : processedEvents[0]
            }
            return event
          }),
    })
  },
  deleteEvents: (eventRef, isBulkDelete) =>
    set({
      events: get().events.filter((event) => {
        if (isBulkDelete) {
          const eventStartDate = startOfDay(parseISO(event.startDate))
          const eventRefStartDate = startOfDay(parseISO(eventRef.startDate))

          const isSameOrAfter =
            isSameDay(eventStartDate, eventRefStartDate) ||
            isAfter(eventStartDate, eventRefStartDate)
          const isSiblingEvent = event.parent.id === eventRef.parent.id

          return !isSiblingEvent || (isSiblingEvent && !isSameOrAfter)
        } else {
          return event.id !== eventRef.id
        }
      }),
    }),
}))

export default useEvents
